import type { UseCase } from 'api/types';
import type { ServerMessage } from 'entities/chat/api/chatClient';
import type { ClientType } from 'entities/onboarding/types';
import { VERTICAL_OPTIONS } from 'entities/onboarding/types';

export type Option = { id: string; title: string; description?: string };

// A previewed order the assistant staged, awaiting the user's confirmation to actually place it.
export type OrderPreview = {
  previewToken: string;
  env: string;
  draft: Record<string, unknown>;
  threadId: string | null;
};

export type Message =
  | { id: string; role: 'system' | 'ai' | 'user'; kind: 'text'; text: string; streaming?: boolean }
  | { id: string; role: 'system'; kind: 'multi'; prompt: string; options: Option[] }
  | {
      id: string;
      role: 'system';
      kind: 'order_preview';
      preview: OrderPreview;
      idempotencyKey: string;
    };

// The wizard runs vertical -> submitting -> checklist (client type is derived, not asked).
// `checklist` is where the assistant takes over and the checklist pane appears above the thread.
export type Phase = 'vertical' | 'submitting' | 'checklist';

export type ConversationState = {
  phase: Phase;
  messages: Message[];
  clientType: ClientType | null;
  verticals: string[];
  pending: string[];
};

export type ConversationAction =
  | { type: 'TOGGLE_VERTICAL'; id: string }
  | { type: 'CONFIRM_VERTICALS' }
  | { type: 'SUBMIT_DONE' }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'SHOW_ORDER_PREVIEW'; preview: OrderPreview }
  | { type: 'LOAD_OLDER'; messages: Message[] }
  | { type: 'ASK_AI'; text: string }
  | { type: 'AI_DELTA'; text: string }
  | { type: 'AI_DONE' }
  | { type: 'AI_ERROR'; message: string };

const mkId = (): string => crypto.randomUUID();

const VERTICAL_CARDS: Option[] = VERTICAL_OPTIONS.map((o) => ({
  id: o.value,
  title: o.label,
  description: o.description,
}));

const VERTICAL_LABELS = new Map(VERTICAL_OPTIONS.map((o) => [o.value as string, o.label]));

function systemText(text: string): Message {
  return { id: mkId(), role: 'system', kind: 'text', text };
}

function userText(text: string): Message {
  return { id: mkId(), role: 'user', kind: 'text', text };
}

function verticalPrompt(): Message {
  return {
    id: mkId(),
    role: 'system',
    kind: 'multi',
    prompt: 'Which verticals are you working in? Select all that apply.',
    options: VERTICAL_CARDS,
  };
}

function streamingIndex(messages: Message[]): number {
  return messages.findIndex(
    (message) => message.kind === 'text' && message.role === 'ai' && message.streaming === true,
  );
}

// Persisted assistant/user turns -> chat bubbles. Tool-only or empty rows have nothing to show, so
// they're dropped (their real ids still page correctly server-side via the raw response).
export function historyToMessages(serverMessages: ServerMessage[]): Message[] {
  const messages: Message[] = [];
  for (const message of serverMessages) {
    const text = message.content || (message.order_group_id ? 'Order placed.' : '');
    if (!text) {
      continue;
    }
    messages.push({ id: message.id, role: message.role === 'assistant' ? 'ai' : 'user', kind: 'text', text });
  }
  return messages;
}

// Just the back-and-forth: the wizard's own prompts, its cards and the order previews aren't part
// of what someone would be raising a support ticket about.
export function serializeConversation(messages: Message[]): string {
  return messages
    .flatMap((message) =>
      message.kind === 'text' && message.role !== 'system'
        ? [`[${message.role === 'user' ? 'User' : 'Truv AI'}]: ${message.text}`]
        : [],
    )
    .join('\n');
}

// What we already know about the user from signup / the post-signup subscription wizard.
// `verticals: null` means the vertical (use_case) was never captured, so the wizard should ask it;
// an array (possibly empty) means it's known and the question is skipped.
export type Prefill = {
  clientType: ClientType;
  verticals: UseCase[] | null;
};

const GREETING = 'Hi! Ask me anything about your Truv dashboard.';
const RETURNING_GREETING = 'Welcome back! Ask me anything, or work through your setup steps above.';
// Said once, with the greeting, rather than hedging every answer.
const AI_CAVEAT = "I'm an AI assistant, so I can get things wrong — double-check anything important.";

const greeting = (intro: string): Message => systemText(`${intro}\n\n${AI_CAVEAT}`);

// Nothing left to ask: the checklist pane carries the setup, so the thread only needs a greeting —
// and not even that when there is history to show, which speaks for itself.
function ready(history: Message[], intro: string): ConversationState {
  return {
    phase: 'checklist',
    messages: history.length ? [...history] : [greeting(intro)],
    clientType: null,
    verticals: [],
    pending: [],
  };
}

export type InitialConversationArgs = {
  wizardComplete: boolean;
  prefill: Prefill;
  history?: Message[];
  checklistEnabled?: boolean;
  /**
   * Whether this member may answer the vertical question.
   *
   * The vertical is company-level config that only an owner can set, so a teammate opening the
   * assistant first is never asked — they get their checklist and the question waits for the owner.
   */
  canSetVertical: boolean;
};

export function initialConversation({
  wizardComplete,
  prefill,
  history = [],
  checklistEnabled = true,
  canSetVertical,
}: InitialConversationArgs): ConversationState {
  // Members who joined before the checklist shipped already know the dashboard, so they get the
  // assistant on its own: no wizard, no checklist, just the thread and the composer.
  if (!checklistEnabled) {
    return ready(history, GREETING);
  }
  if (wizardComplete) {
    return ready(history, RETURNING_GREETING);
  }
  // A teammate has nothing to contribute here, so their setup is submitted empty rather than left
  // unanswered — the checklist only loads once this membership's wizard is marked complete.
  const verticals = prefill.verticals ?? (canSetVertical ? null : []);
  // Nothing left to ask -> submit silently and go straight to the checklist. The setup call is
  // invisible to the user, so the thread opens on the ordinary greeting rather than narrating it.
  if (verticals !== null) {
    return {
      phase: 'submitting',
      messages: history.length ? [...history] : [greeting(GREETING)],
      clientType: prefill.clientType,
      verticals,
      pending: verticals,
    };
  }
  // Account type is known; only the vertical is missing (e.g. the subscription wizard was skipped).
  return {
    phase: 'vertical',
    messages: [...history, systemText("Hi! Let's get you set up. One quick question."), verticalPrompt()],
    clientType: prefill.clientType,
    verticals: [],
    pending: [],
  };
}

export function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'TOGGLE_VERTICAL': {
      const pending = state.pending.includes(action.id)
        ? state.pending.filter((id) => id !== action.id)
        : [...state.pending, action.id];
      return { ...state, pending };
    }

    case 'CONFIRM_VERTICALS': {
      const labels = state.pending.map((id) => VERTICAL_LABELS.get(id) ?? id);
      return {
        ...state,
        verticals: state.pending,
        phase: 'submitting',
        messages: [...state.messages, userText(labels.length ? labels.join(', ') : 'Skip for now')],
      };
    }

    case 'SUBMIT_DONE':
      return { ...state, phase: 'checklist' };

    case 'SUBMIT_ERROR': {
      // If we submitted silently (no vertical question was ever shown), surface it now so the user
      // has a way to retry via CONFIRM_VERTICALS.
      const needsPrompt = !state.messages.some((message) => message.kind === 'multi');
      return {
        ...state,
        phase: 'vertical',
        messages: [
          ...state.messages,
          systemText("Something went wrong saving that. Let's try again."),
          ...(needsPrompt ? [verticalPrompt()] : []),
        ],
      };
    }

    case 'SHOW_ORDER_PREVIEW': {
      // Skip a duplicate preview for the same token, and derive a stable idempotency key from it so a
      // replayed preview can't slip past confirmOrder's idempotency check with a fresh key.
      const alreadyShown = state.messages.some(
        (message) => message.kind === 'order_preview' && message.preview.previewToken === action.preview.previewToken,
      );
      if (alreadyShown) {
        return state;
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: mkId(),
            role: 'system',
            kind: 'order_preview',
            preview: action.preview,
            idempotencyKey: action.preview.previewToken,
          },
        ],
      };
    }

    case 'LOAD_OLDER':
      return { ...state, messages: [...action.messages, ...state.messages] };

    case 'ASK_AI':
      return {
        ...state,
        messages: [
          ...state.messages,
          userText(action.text),
          { id: mkId(), role: 'ai', kind: 'text', text: '', streaming: true },
        ],
      };

    case 'AI_DELTA': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, text: msg.text + action.text };
      }
      return { ...state, messages };
    }

    case 'AI_DONE': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, streaming: false };
      }
      return { ...state, messages };
    }

    case 'AI_ERROR': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, text: action.message, streaming: false };
      }
      return { ...state, messages };
    }

    default:
      return state;
  }
}
